/**
 * Validation utilities for opportunity forms
 */

export interface AmountValidationResult {
  isValid: boolean
  errorMessage?: string
}

/**
 * Validates that a number is positive or empty
 */
export const validatePositiveAmount = (
  amount: string | null,
  fieldName: string
): AmountValidationResult => {
  if (amount === '' || amount === null) {
    return { isValid: true }
  }

  const numValue = Number(amount)
  if (isNaN(numValue) || numValue <= 0) {
    return {
      isValid: false,
      errorMessage: `${fieldName} must be a valid positive number`,
    }
  }

  return { isValid: true }
}

/**
 * Validates that max amount is greater than or equal to min amount
 */
export const validateAmountRange = (
  minAmount: string | null,
  maxAmount: string | null
): AmountValidationResult => {
  if (
    minAmount !== '' &&
    minAmount !== null &&
    maxAmount !== '' &&
    maxAmount !== null &&
    Number(maxAmount) < Number(minAmount)
  ) {
    return {
      isValid: false,
      errorMessage: 'Max amount cannot be lower than min amount.',
    }
  }

  return { isValid: true }
}

/**
 * Normalizes amount values for API submission
 * - Converts empty strings to null
 * - Sets both to null if no_min_max_amount is true
 */
export const normalizeAmounts = (
  minAmount: string | null,
  maxAmount: string | null,
  noMinMaxAmount: boolean
): { min_amount: string | null; max_amount: string | null } => {
  if (noMinMaxAmount) {
    return { min_amount: null, max_amount: null }
  }

  return {
    min_amount: minAmount === '' ? null : minAmount,
    max_amount: maxAmount === '' ? null : maxAmount,
  }
}

/**
 * Transforms questions string to array
 * - Splits by double newline
 * - Trims end of each line
 * - Filters out empty strings
 */
export const transformQuestions = (questionsString: string): string[] => {
  if (!questionsString) {
    return []
  }

  return questionsString
    .split('\n\n')
    .map((line: string) => line.trimEnd())
    .filter((line: string) => line !== '')
}

/**
 * Trims fund name
 */
export const normalizeFundName = (fundName: string): string => {
  return fundName.trim()
}
